import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface AlertScheduleEditorProps {
  value: number[]
  onChange: (next: number[]) => void
  disabled?: boolean
}

/**
 * The days-before-expiry schedule, as chips rather than a comma-separated text field.
 *
 * <p>A text field would accept "90, 60, thirty" and "90,60,60,-1", and the person typing it
 * would find out at save time, or not at all. Chips make each threshold a thing you can see
 * and remove, and the input can refuse a bad one at the moment it is added.
 *
 * <p>Sorted descending on every change, because the sweep walks them furthest-first and an
 * out-of-order list would read as though the 7-day warning came before the 90-day one.
 */
export function AlertScheduleEditor({ value, onChange, disabled }: AlertScheduleEditorProps) {
  function add(raw: string) {
    const days = Number(raw)
    if (!Number.isInteger(days) || days <= 0 || value.includes(days)) {
      return false
    }
    onChange([...value, days].sort((a, b) => b - a))
    return true
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="alert-threshold">Warn this many days before expiry</Label>
      <div className="flex flex-wrap items-center gap-1.5">
        {value.length === 0 && (
          <span className="text-xs text-[var(--color-muted)]">
            No warnings — this type is recorded for the file only.
          </span>
        )}
        {value.map((days) => (
          <span
            key={days}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary-soft)] py-0.5 pe-1 ps-2.5 text-xs font-medium text-[var(--color-primary)]"
          >
            {days} days
            <button
              type="button"
              disabled={disabled}
              aria-label={`Remove the ${days} day warning`}
              className="grid size-4 place-items-center rounded-full hover:bg-black/10 disabled:opacity-50"
              onClick={() => onChange(value.filter((existing) => existing !== days))}
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          id="alert-threshold"
          type="number"
          min={1}
          placeholder="e.g. 30"
          disabled={disabled}
          className="w-32"
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            // Enter inside a dialog would otherwise submit the form with the number still
            // sitting unadded in the box.
            event.preventDefault()
            const input = event.currentTarget
            if (add(input.value)) input.value = ''
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={(event) => {
            const input = event.currentTarget.previousElementSibling as HTMLInputElement | null
            if (input && add(input.value)) input.value = ''
          }}
        >
          Add
        </Button>
      </div>
      <p className="text-xs text-[var(--color-muted)]">
        A warning also goes out on the day it expires, whatever this schedule says.
      </p>
    </div>
  )
}
